import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiCall } from '@/AuthContext';
import { motion } from 'framer-motion';
import { Shield, LogOut, Users, CreditCard, Calendar, UserPlus, BookOpen, Search, CheckCircle, Clock, XCircle, ChevronDown, MessageSquare, Printer, Copy, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getWhatsAppReminderUrl, getBulkReminderData } from '@/utils/whatsapp';
import { downloadReceiptPDF } from '@/utils/receiptPdf';

const EDUCATION_OPTIONS = ['SSLC', 'Plus Two', 'Degree', 'PG', 'Diploma', 'LLB', 'Other Professional Degree'];

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fees, setFees] = useState([]);
  const [search, setSearch] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showPayFee, setShowPayFee] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBatch, setSelectedBatch] = useState('regular');
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [batchStudents, setBatchStudents] = useState([]);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, batchesRes, feesRes, enquiriesRes] = await Promise.all([
        apiCall('get', '/students'),
        apiCall('get', '/batches'),
        apiCall('get', '/fees'),
        apiCall('get', '/enquiries'),
      ]);
      setStudents(studentsRes.data);
      setBatches(batchesRes.data);
      setFees(feesRes.data);
      setEnquiries(enquiriesRes.data);
    } catch {
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const filtered = students.filter(s => s.batch_id === selectedBatch && s.status === 'active');
    setBatchStudents(filtered);
    const records = {};
    filtered.forEach(s => { records[s.student_id] = 'present'; });
    setAttendanceRecords(records);
  }, [selectedBatch, students]);

  const filteredStudents = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(search.toLowerCase()) ||
    s.phone?.includes(search)
  );

  const tabs = [
    { id: 'students', label: 'Students', icon: Users },
    { id: 'fees', label: 'Fee Collection', icon: CreditCard },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare },
  ];

  // Registration Form
  const [regForm, setRegForm] = useState({
    name: '', dob: '', address: '', marital_status: 'single', phone: '',
    educational_qualification: 'SSLC', batch_id: 'regular', monthly_fee: 800, admission_fee: 0, email: '',
    username: '', password: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const res = await apiCall('post', '/students/register', regForm);
      toast.success(`Student registered! ID: ${res.data.student_id}`);
      setShowRegister(false);
      setRegForm({ name: '', dob: '', address: '', marital_status: 'single', phone: '', educational_qualification: 'SSLC', batch_id: 'regular', monthly_fee: 800, admission_fee: 0, email: '', username: '', password: '' });
      setCredentials({ username: res.data.username, password: res.data.password, student_id: res.data.student_id, name: res.data.name, login_email: res.data.login_email });
      setShowCredentials(true);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    }
  };

  // Fee Payment
  const [payForm, setPayForm] = useState({ student_id: '', month: '', year: new Date().getFullYear(), amount: 0, payment_method: 'cash' });
  const [payMode, setPayMode] = useState('full'); // full | partial (staff cannot waive — admin only)
  const [partialAmount, setPartialAmount] = useState(0);

  const openPayFee = (studentId) => {
    const studentFees = fees.filter(f => f.student_id === studentId && ['pending','partial'].includes(f.status));
    setSelectedStudent(studentId);
    if (studentFees.length > 0) {
      const f = studentFees[0];
      const balance = Math.max(0, (f.amount || 0) - (f.amount_paid || 0));
      setPayForm({ student_id: studentId, month: f.month, year: f.year, amount: f.amount, payment_method: 'cash' });
      setPartialAmount(balance);
      setPayMode(f.status === 'partial' ? 'partial' : 'full');
    }
    setShowPayFee(true);
  };

  const handlePayFee = async (e) => {
    e.preventDefault();
    try {
      if (payMode === 'partial') {
        const amt = parseFloat(partialAmount) || 0;
        if (amt <= 0) { toast.error('Enter a valid partial amount'); return; }
        await apiCall('post', '/fees/partial-pay', { student_id: payForm.student_id, month: payForm.month, year: payForm.year, amount: amt, payment_method: payForm.payment_method, notes: '' });
        toast.success(`Partial ₹${amt} recorded`);
      } else {
        await apiCall('post', '/fees/pay', payForm);
        toast.success('Fee payment recorded (Full)');
      }
      setShowPayFee(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    }
  };

  const viewReceipt = async (studentId, month, year) => {
    try {
      const res = await apiCall('get', `/fees/receipt/${studentId}/${month}/${year}`);
      setReceipt(res.data);
      setShowReceipt(true);
    } catch { toast.error('Receipt not available'); }
  };

  const handleDownloadPDF = () => {
    if (receipt) { downloadReceiptPDF(receipt); toast.success('Receipt PDF downloaded'); }
  };

  // Enquiry form
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', course_interest: '', notes: '', source: 'walk-in' });
  const handleAddEnquiry = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/enquiries', enquiryForm);
      toast.success('Enquiry added');
      setShowEnquiry(false);
      setEnquiryForm({ name: '', phone: '', email: '', course_interest: '', notes: '', source: 'walk-in' });
      fetchData();
    } catch { toast.error('Failed to add enquiry'); }
  };

  const pauseStudent = async (studentId, name) => {
    if (!window.confirm(`Pause ${name}?`)) return;
    try { const res = await apiCall('post', `/students/${studentId}/pause`); toast.success(res.data.message); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resumeStudent = async (studentId, name) => {
    if (!window.confirm(`Resume ${name}?`)) return;
    try { const res = await apiCall('post', `/students/${studentId}/resume`); toast.success(res.data.message); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [showBulkReminder, setShowBulkReminder] = useState(false);

  const sendWhatsAppReminder = (fee) => {
    const student = students.find(s => s.student_id === fee.student_id);
    if (!student?.phone) { toast.error('Student phone not found'); return; }
    const url = getWhatsAppReminderUrl(student.phone, student.name, fee.amount, fee.month, fee.year, fee.type);
    window.open(url, '_blank');
    toast.success(`WhatsApp opened for ${student.name}`);
  };

  const bulkReminders = getBulkReminderData(fees, students);

  // Bulk Attendance
  const handleBulkAttendance = async () => {
    const records = Object.entries(attendanceRecords).map(([student_id, status]) => ({ student_id, status }));
    try {
      await apiCall('post', '/attendance/bulk', { batch_id: selectedBatch, date: attendanceDate, records });
      toast.success(`Attendance marked for ${records.length} students`);
    } catch {
      toast.error('Failed to mark attendance');
    }
  };

  const generateMonthlyFees = async () => {
    try {
      const res = await apiCall('post', '/fees/generate-monthly');
      toast.success(res.data.message);
      fetchData();
    } catch {
      toast.error('Failed to generate fees');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="staff-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_signature-psc-portal/artifacts/h1jchf2q_IMG-20260126-WA0188.jpg" alt="Logo" className="w-8 h-8 rounded-md object-contain bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Staff Portal</span>
              <span className="text-[10px] block text-[#a1a1aa]">{user?.name}</span>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#a1a1aa] hover:text-white hover:bg-[#27272a]" data-testid="staff-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Branch banner — Staff scope indicator */}
        {user?.assigned_branch && (
          <div className="mb-5 p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-[#d4af37]/10 border border-emerald-500/30 flex items-center gap-3" data-testid="staff-branch-banner">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-300 font-bold text-sm">
              {(user.assigned_branch || 'p').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase text-emerald-300 tracking-wider font-semibold">Your Branch</p>
              <p className="text-sm text-white font-bold capitalize">{user.assigned_branch}</p>
            </div>
            <span className="text-[10px] text-[#a1a1aa]">Showing only {user.assigned_branch} students & accounts</span>
          </div>
        )}
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] hover:bg-[#27272a] border border-[#27272a]'}`}
              data-testid={`staff-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* STUDENTS TAB */}
        {tab === 'students' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
                <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-10 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]"
                  data-testid="student-search" />
              </div>
              <Button onClick={() => setShowRegister(true)}
                className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold shadow-[0_0_15px_rgba(212,175,55,0.3)]"
                data-testid="register-student-btn">
                <UserPlus className="w-4 h-4 mr-2" /> New Admission
              </Button>
            </div>

            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="students-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['ID', 'Name', 'Phone', 'Batch', 'Plan', 'Status', 'Actions'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-[#52525b]">No students found</td></tr>
                    ) : (
                      filteredStudents.map((s, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-[#d4af37] font-mono">{s.student_id}</td>
                          <td className="p-4 text-sm text-white">{s.name}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{s.phone}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{s.batch_id}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">₹{s.monthly_fee || 800}</td>
                          <td className="p-4">
                            <Badge className={s.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : s.status === 'paused' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}>
                              {s.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openPayFee(s.student_id)}
                                className="text-[#d4af37] hover:bg-[#d4af37]/10 text-xs" data-testid={`pay-fee-${s.student_id}`}>
                                <CreditCard className="w-3 h-3 mr-1" /> Fee
                              </Button>
                              {s.status === 'active' && (
                                <Button size="sm" variant="ghost" onClick={() => pauseStudent(s.student_id, s.name)}
                                  className="text-amber-400 hover:bg-amber-500/10 text-xs">Pause</Button>
                              )}
                              {s.status === 'paused' && (
                                <Button size="sm" variant="ghost" onClick={() => resumeStudent(s.student_id, s.name)}
                                  className="text-emerald-400 hover:bg-emerald-500/10 text-xs">Resume</Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* FEES TAB */}
        {tab === 'fees' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-wrap gap-4">
              <Button onClick={generateMonthlyFees}
                className="bg-[#047857] text-white hover:bg-[#059669] font-semibold"
                data-testid="generate-fees-btn">
                <CreditCard className="w-4 h-4 mr-2" /> Generate Monthly Fees
              </Button>
              <Button onClick={() => setShowBulkReminder(true)}
                className="bg-[#25D366] text-white hover:bg-[#128C7E] font-semibold"
                data-testid="bulk-whatsapp-btn">
                <Send className="w-4 h-4 mr-2" /> WhatsApp Reminders
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="fee-collected-card">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Collected</p>
                <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{fees.filter(f => f.status === 'paid').reduce((s, f) => s + f.amount, 0)}
                </p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="fee-pending-card">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Pending</p>
                <p className="text-2xl font-bold text-red-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{fees.filter(f => f.status === 'pending').reduce((s, f) => s + f.amount, 0)}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="fees-list">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Pending Fees</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Student ID', 'Month', 'Amount', 'Type', 'Status', 'Action'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fees.filter(f => f.status === 'pending').length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No pending fees</td></tr>
                    ) : (
                      fees.filter(f => f.status === 'pending').map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-[#d4af37] font-mono">{f.student_id}</td>
                          <td className="p-4 text-sm text-white">{f.month} {f.year}</td>
                          <td className="p-4 text-sm text-white font-medium">₹{f.amount}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                          <td className="p-4">
                            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                              <Clock className="w-3 h-3 mr-1" /> pending
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1">
                              <Button size="sm" onClick={() => openPayFee(f.student_id)}
                                className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] text-xs font-semibold"
                                data-testid={`collect-fee-${f.student_id}-${f.month}`}>
                                Collect
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => sendWhatsAppReminder(f)}
                                className="text-[#25D366] hover:bg-[#25D366]/10 text-xs"
                                data-testid={`whatsapp-${f.student_id}-${f.month}`}>
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paid Fees with Receipt */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="paid-fees-list">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Paid Fees (Recent)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Student ID', 'Month', 'Amount', 'Type', 'Status', 'Receipt'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fees.filter(f => f.status === 'paid').length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No paid fees</td></tr>
                    ) : (
                      fees.filter(f => f.status === 'paid').slice(0, 20).map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-[#d4af37] font-mono">{f.student_id}</td>
                          <td className="p-4 text-sm text-white">{f.month} {f.year}</td>
                          <td className="p-4 text-sm text-white font-medium">₹{f.amount}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                          <td className="p-4"><Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" /> paid</Badge></td>
                          <td className="p-4">
                            <Button size="sm" variant="ghost" onClick={() => viewReceipt(f.student_id, f.month, f.year)}
                              className="text-[#d4af37] hover:bg-[#d4af37]/10 text-xs" data-testid={`receipt-${f.student_id}-${f.month}`}>
                              <Printer className="w-3 h-3 mr-1" /> Receipt
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* ATTENDANCE TAB */}
        {tab === 'attendance' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="w-48">
                <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider mb-1 block">Date</Label>
                <Input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)}
                  className="bg-[#18181b] border-[#27272a] text-white focus:border-[#d4af37]"
                  data-testid="attendance-date" />
              </div>
              <div className="w-48">
                <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider mb-1 block">Batch</Label>
                <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                  <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white" data-testid="attendance-batch-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {batches.map(b => (
                      <SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:mt-5">
                <Button onClick={handleBulkAttendance}
                  className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold shadow-[0_0_15px_rgba(212,175,55,0.3)]"
                  data-testid="submit-attendance-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Save Attendance
                </Button>
              </div>
            </div>

            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="attendance-marking-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Mark Attendance — {selectedBatch.charAt(0).toUpperCase() + selectedBatch.slice(1)} Batch
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Student ID', 'Name', 'Status'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {batchStudents.length === 0 ? (
                      <tr><td colSpan={3} className="p-8 text-center text-[#52525b]">No students in this batch</td></tr>
                    ) : (
                      batchStudents.map((s, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-[#d4af37] font-mono">{s.student_id}</td>
                          <td className="p-4 text-sm text-white">{s.name}</td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              {['present', 'late', 'leave', 'absent'].map(status => (
                                <button key={status} onClick={() => setAttendanceRecords(prev => ({ ...prev, [s.student_id]: status }))}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-all ${attendanceRecords[s.student_id] === status
                                    ? (status === 'present' ? 'bg-emerald-500 text-white' : status === 'late' ? 'bg-amber-500 text-white' : status === 'leave' ? 'bg-violet-500 text-white' : 'bg-red-500 text-white')
                                    : 'bg-[#27272a] text-[#a1a1aa] hover:bg-[#3f3f46]'
                                  }`}
                                  data-testid={`attendance-${s.student_id}-${status}`}>
                                  {status === 'present' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : status === 'late' ? <Clock className="w-3 h-3 inline mr-1" /> : status === 'leave' ? '📋 ' : <XCircle className="w-3 h-3 inline mr-1" />}
                                  {status}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* ENQUIRIES TAB */}
        {tab === 'enquiries' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-end">
              <Button onClick={() => setShowEnquiry(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="add-enquiry-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Add Enquiry
              </Button>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="enquiries-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Enquiries</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Name', 'Phone', 'Course Interest', 'Source', 'Status', 'Date'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No enquiries yet</td></tr>
                    ) : (
                      enquiries.map((eq, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{eq.name}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{eq.phone}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{eq.course_interest || '-'}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{eq.source}</td>
                          <td className="p-4">
                            <Badge className={eq.status === 'new' ? 'bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}>
                              {eq.status}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-[#52525b]">{eq.created_at?.split('T')[0]}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>New Student Admission</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Full Name *</Label>
                <Input value={regForm.name} onChange={e => setRegForm({ ...regForm, name: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="reg-name" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Date of Birth *</Label>
                <Input type="date" value={regForm.dob} onChange={e => setRegForm({ ...regForm, dob: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="reg-dob" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Phone *</Label>
                <Input value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="reg-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Email (Optional)</Label>
                <Input type="email" value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="reg-email" />
              </div>
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Address *</Label>
                <Input value={regForm.address} onChange={e => setRegForm({ ...regForm, address: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="reg-address" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Marital Status</Label>
                <Select value={regForm.marital_status} onValueChange={v => setRegForm({ ...regForm, marital_status: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="reg-marital">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="single" className="text-white hover:bg-[#27272a]">Single</SelectItem>
                    <SelectItem value="married" className="text-white hover:bg-[#27272a]">Married</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-[#27272a]">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Education *</Label>
                <Select value={regForm.educational_qualification} onValueChange={v => setRegForm({ ...regForm, educational_qualification: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="reg-education">
                    <SelectValue placeholder="Select qualification" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {EDUCATION_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-[#27272a]">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Batch *</Label>
                <Select value={regForm.batch_id} onValueChange={v => setRegForm({ ...regForm, batch_id: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="reg-batch">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {batches.map(b => (
                      <SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Monthly Fee *</Label>
                <Select value={String(regForm.monthly_fee)} onValueChange={v => setRegForm({ ...regForm, monthly_fee: parseInt(v) })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="reg-monthly-fee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="800" className="text-white hover:bg-[#27272a]">₹800 / month</SelectItem>
                    <SelectItem value="1000" className="text-white hover:bg-[#27272a]">₹1000 / month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Admission Fee</Label>
                <Select value={String(regForm.admission_fee)} onValueChange={v => setRegForm({ ...regForm, admission_fee: parseInt(v) })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="reg-admission-fee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="0" className="text-white hover:bg-[#27272a]">₹0 (No Admission Fee)</SelectItem>
                    <SelectItem value="1000" className="text-white hover:bg-[#27272a]">₹1000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Username (Login ID)</Label>
                <Input value={regForm.username} onChange={e => setRegForm({ ...regForm, username: e.target.value })}
                  placeholder="e.g. student phone or name"
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="reg-username" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Password</Label>
                <Input value={regForm.password} onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                  placeholder="Simple password"
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="reg-password" />
              </div>
            </div>
            <p className="text-xs text-[#52525b]">If username/password left empty, phone number will be used as username and last 4 digits as password.</p>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="reg-submit-btn">
              Register Student
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pay Fee Dialog */}
      <Dialog open={showPayFee} onOpenChange={setShowPayFee}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="pay-fee-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Record Fee Payment</DialogTitle>
          </DialogHeader>
          <div className="mb-3">
            <p className="text-sm text-[#a1a1aa]">Student: <span className="text-[#d4af37] font-mono">{selectedStudent}</span></p>
            <div className="mt-3 space-y-2">
              {fees.filter(f => f.student_id === selectedStudent && ['pending','partial'].includes(f.status)).map((f, i) => {
                const balance = Math.max(0, (f.amount || 0) - (f.amount_paid || 0));
                return (
                  <button key={i} type="button" onClick={() => { setPayForm({ ...payForm, month: f.month, year: f.year, amount: f.amount }); setPartialAmount(balance); }}
                    className={`w-full text-left p-3 rounded-md border transition-all ${payForm.month === f.month && payForm.year === f.year ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-[#27272a] bg-[#09090b]'}`}
                    data-testid={`select-fee-${f.month}-${f.year}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white capitalize">{f.month} {f.year} ({f.type}){f.status === 'partial' && <span className="text-blue-300 text-xs ml-1">· partial</span>}</span>
                      <span className="text-sm font-bold text-[#d4af37]">₹{f.amount}</span>
                    </div>
                    {(f.amount_paid > 0) && <p className="text-[10px] text-emerald-400 mt-0.5">Paid ₹{f.amount_paid} · Balance ₹{balance}</p>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-[#09090b] border border-[#27272a] mb-3">
            {[['full','Full Payment'], ['partial','Partial Payment']].map(([m, lbl]) => (
              <button key={m} type="button" onClick={() => setPayMode(m)}
                className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${payMode === m ? 'bg-[#d4af37] text-[#18181b]' : 'text-[#a1a1aa] hover:bg-[#27272a]'}`}
                data-testid={`staff-pay-mode-${m}`}>
                {lbl}
              </button>
            ))}
          </div>
          <form onSubmit={handlePayFee} className="space-y-3">
            {payMode === 'partial' && (
              <div>
                <Label className="text-[#a1a1aa] text-xs">Amount to Collect Now (₹)</Label>
                <Input type="number" min="1" value={partialAmount} onChange={e => setPartialAmount(e.target.value)}
                  className="bg-[#09090b] border-[#27272a] text-white" data-testid="staff-partial-amount" required />
              </div>
            )}
            <div>
              <Label className="text-[#a1a1aa] text-xs">Payment Method</Label>
              <Select value={payForm.payment_method} onValueChange={v => setPayForm({ ...payForm, payment_method: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="cash" className="text-white hover:bg-[#27272a]">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-[#27272a]">UPI</SelectItem>
                  <SelectItem value="bank_transfer" className="text-white hover:bg-[#27272a]">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="pay-submit-btn">
              {payMode === 'partial' ? `Collect Partial ₹${partialAmount || 0}` : `Collect Full ₹${payForm.amount}`}
            </Button>
            <p className="text-[10px] text-[#52525b] text-center">Only Admin can waive off a fee.</p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentials} onOpenChange={setShowCredentials}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Student Login Credentials</DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4">
              <div className="p-4 rounded-md bg-[#09090b] border border-[#d4af37]/30">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-2">Student Login Credentials</p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#a1a1aa]">Student ID</span>
                    <span className="text-sm text-[#d4af37] font-mono">{credentials.student_id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#a1a1aa]">Name</span>
                    <span className="text-sm text-white">{credentials.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#a1a1aa]">Username</span>
                    <span className="text-sm text-white font-mono">{credentials.username}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#a1a1aa]">Password</span>
                    <span className="text-sm text-white font-mono">{credentials.password}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-[#52525b]">Share these credentials with the student. They can login using Username or Email.</p>
              <Button onClick={() => {
                navigator.clipboard.writeText(`Username: ${credentials.username}\nPassword: ${credentials.password}`);
                toast.success('Credentials copied!');
              }} className="w-full bg-[#27272a] text-white hover:bg-[#3f3f46]" data-testid="copy-credentials-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy Credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Dialog */}
      <Dialog open={showEnquiry} onOpenChange={setShowEnquiry}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="enquiry-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Enquiry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEnquiry} className="space-y-4">
            <div>
              <Label className="text-[#a1a1aa] text-xs">Name *</Label>
              <Input value={enquiryForm.name} onChange={e => setEnquiryForm({ ...enquiryForm, name: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="enquiry-name" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Phone *</Label>
              <Input value={enquiryForm.phone} onChange={e => setEnquiryForm({ ...enquiryForm, phone: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="enquiry-phone" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Email</Label>
              <Input value={enquiryForm.email} onChange={e => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="enquiry-email" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Course Interest</Label>
              <Input value={enquiryForm.course_interest} onChange={e => setEnquiryForm({ ...enquiryForm, course_interest: e.target.value })}
                placeholder="e.g., LDC, LGS, Company Board..."
                className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="enquiry-course" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Source</Label>
              <Select value={enquiryForm.source} onValueChange={v => setEnquiryForm({ ...enquiryForm, source: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="enquiry-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="walk-in" className="text-white hover:bg-[#27272a]">Walk-in</SelectItem>
                  <SelectItem value="phone" className="text-white hover:bg-[#27272a]">Phone Call</SelectItem>
                  <SelectItem value="referral" className="text-white hover:bg-[#27272a]">Referral</SelectItem>
                  <SelectItem value="website" className="text-white hover:bg-[#27272a]">Website</SelectItem>
                  <SelectItem value="social_media" className="text-white hover:bg-[#27272a]">Social Media</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Notes</Label>
              <Textarea value={enquiryForm.notes} onChange={e => setEnquiryForm({ ...enquiryForm, notes: e.target.value })} rows={3}
                className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37] resize-none" data-testid="enquiry-notes" />
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="enquiry-submit-btn">
              Add Enquiry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp Reminder Dialog */}
      <Dialog open={showBulkReminder} onOpenChange={setShowBulkReminder}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp Fee Reminders
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#a1a1aa] mb-4">Click on a student to open WhatsApp with a pre-filled fee reminder message.</p>
          {bulkReminders.length === 0 ? (
            <p className="text-center text-[#52525b] py-6">No pending fees to remind</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              <p className="text-xs text-[#d4af37] font-medium">{bulkReminders.length} pending reminders</p>
              {bulkReminders.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-md bg-[#09090b] border border-[#27272a] hover:border-[#25D366]/50 transition-all group cursor-pointer"
                  data-testid={`reminder-${r.student_id}`}>
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">{r.name}</p>
                    <p className="text-xs text-[#a1a1aa]">{r.phone} — {r.month} {r.year} ({r.type})</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#d4af37]">₹{r.amount}</span>
                    <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                      <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="staff-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Fee Receipt</DialogTitle>
          </DialogHeader>
          {receipt && (
            <>
              <div ref={receiptRef} className="bg-white text-black p-6 rounded-md">
                <div className="header text-center border-b-2 border-[#d4af37] pb-3 mb-4">
                  <h1 className="text-lg font-bold text-black">{receipt.centre_name}</h1>
                  <p className="text-xs text-gray-600">{receipt.centre_address}</p>
                  <p className="text-xs text-gray-600">Phone: {receipt.centre_phone}</p>
                </div>
                <p className="text-right text-xs text-gray-500 mb-3">Receipt No: {receipt.receipt_no}</p>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Student ID</td><td className="py-2 text-right text-black">{receipt.student_id}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Student Name</td><td className="py-2 text-right text-black">{receipt.student_name}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Month / Year</td><td className="py-2 text-right text-black">{receipt.month} {receipt.year}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Fee Type</td><td className="py-2 text-right capitalize text-black">{receipt.type}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Payment</td><td className="py-2 text-right capitalize text-black">{receipt.payment_method}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Paid Date</td><td className="py-2 text-right text-black">{receipt.paid_date?.split('T')[0]}</td></tr>
                  </tbody>
                </table>
                <div className="text-center py-4 mt-3 bg-gray-50 border border-[#d4af37] rounded">
                  <span className="text-2xl font-bold text-black">₹{receipt.amount}</span>
                </div>
              </div>
              <Button onClick={handleDownloadPDF} className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold mt-3" data-testid="staff-download-receipt-pdf">
                <Printer className="w-4 h-4 mr-2" /> Download PDF Receipt
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
